import React from 'react';
import { useTranslation } from 'react-i18next';
import { XStack, YStack, Text } from 'tamagui';
import { tokens } from 'tamagui-config';

import { GlowCircle } from '../../../components/common/GlowCircle';
import { InvalidConfiguration } from '../../../components/common/InvalidConfiguration';
import { LoadingOverlay } from '../../../components/common/LoadingOverlay';
import { useGetEnergySavingData } from '../../../hooks';

export const TodaysEnergyCostContent: React.FC = () => {
  const { t } = useTranslation();
  const { data, loading } = useGetEnergySavingData();

  return (
    <YStack gap="$4" height="90%" justifyContent="center">
      <LoadingOverlay isVisible={loading} />

      {!loading && !data && <InvalidConfiguration />}

      {!loading &&
        data &&
        (() => {
          // Extract today's data if it exists
          const today = new Date();
          const todayDateString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`; // Format: YYYY-MM-DD in local timezone
          const todaysData = data.find((item) => item.label === todayDateString);
          const filteredData = data.filter((item) => item.label !== todayDateString);

          // Sort filtered data chronologically
          const sortedData = [...filteredData].sort(
            (a, b) => new Date(a.label).getTime() - new Date(b.label).getTime(),
          );

          // Split data into two weeks: first half and second half
          const midPoint = Math.ceil(sortedData.length - 7);
          const firstWeekData = sortedData.slice(0, midPoint);

          // Calculate percentage change compared to same day last week
          const calculatePercentageChange = () => {
            if (!todaysData) return null;

            const todayDayOfWeek = today.getDay();
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const todayDayName = dayNames[todayDayOfWeek];

            // Find the same day of week from last week's data
            const lastWeekSameDay = firstWeekData.find((item) => {
              const itemDate = new Date(item.label);
              const itemDayName = dayNames[itemDate.getDay()];
              return itemDayName === todayDayName;
            });

            if (!lastWeekSameDay) return null;

            const percentChange =
              ((todaysData.value - lastWeekSameDay.value) / lastWeekSameDay.value) * 100;
            return percentChange;
          };

          const percentageChange = calculatePercentageChange();
          const isIncrease = percentageChange !== null && percentageChange >= 0;

          return (
            <>
              <YStack gap="$4">
                {/* Main content matching mobile layout */}
                <XStack alignItems="center" gap="$2">
                  <YStack alignItems="center" justifyContent="center" position="relative">
                    <GlowCircle
                      size={160}
                      color={isIncrease ? tokens.color.warningRed : tokens.color.successGreen}
                      opacity={0.1}
                      style={{ position: 'absolute', zIndex: 0 }}
                    />                   
                  </YStack>
                  <Text fontSize="$12" fontWeight="$9" color="$white12">
                    ${todaysData ? todaysData.value.toFixed(2) : '0.00'}
                  </Text>
                </XStack>

                {/* Percentage change display */}
                <XStack alignItems="center" gap="$1">
                  {percentageChange !== null && (
                    <>
                      <Text
                        color={isIncrease ? tokens.color.warningRed : tokens.color.successGreen}
                        fontSize="$6"
                        fontWeight="$9">
                        {Math.abs(percentageChange).toFixed(0)}%{' '}
                        {isIncrease ? t('electrical.more') : t('electrical.less')}
                      </Text>
                      <Text color="$white12" fontSize="$5">
                        {t('electrical.thanLastWeek')}
                      </Text>
                    </>
                  )}
                </XStack>
              </YStack>
            </>
          );
        })()}
    </YStack>
  );
};
